import useSWRInfinite, { type SWRInfiniteKeyedMutator } from 'swr/infinite'
import { useState, useEffect, useRef } from 'react'
import {
  Layout,
  PaletteIcon,
  LockIcon,
  EllipsisVertical,
  Trash2,
} from 'lucide-react'
import { format } from 'date-fns'
import Link from 'next/link'
import { useTranslations } from 'next-intl'

import type { UserProject } from '@/lib/types'
import { cn, fetcher } from '@/lib/utils'
import { useCurrentUser } from '@/hooks/use-current-user'
import { toast } from 'sonner'
import { ProjectListSkeleton } from './project-skeleton'
import { pushModal } from '@/components/modals'
import { LoadMoreBtn } from '../load-more-btn'
import sendToMixpanel from '@/lib/mixpanel/sendToMixpanel'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useGlobalStore } from '@/hooks/use-global-store'

const FILTERS = [
  {
    id: 'all',
    label: 'All Projects',
    color: 'from-gray-500 to-gray-600',
    bgColor: 'bg-gray-500/10',
    hoverColor: 'hover:bg-gray-500/20',
  },
  {
    id: 'featured',
    label: 'Featured',
    color: 'from-purple-500 to-purple-600',
    bgColor: 'bg-purple-500/10',
    hoverColor: 'hover:bg-purple-500/20',
  },
]

export const LatestProjectList = ({
  transparent = false,
  showTitle = true,
}: { transparent?: boolean; showTitle?: boolean }) => {
  const { data: user } = useCurrentUser()
  const limit = 20 // Default projects per page
  const [filter, setFilter] = useState('all')
  const t = useTranslations('projects')

  // Pagination with SWR Infinite
  const { data, error, size, setSize, isLoading, isValidating } =
    useSWRInfinite(
      (pageIndex) => {
        const params = new URLSearchParams({
          limit: limit.toString(),
          offset: (pageIndex * limit).toString(),
          ...(filter === 'featured' && { is_featured: 'true' }),
        })
        return `/api/projects/v2?${params}`
      },
      fetcher,
      {
        revalidateFirstPage: false,
        parallel: true,
        initialSize: 3,
      },
    )

  const projects = data ? data.flat() : []
  const isReachingEnd = data && data[data.length - 1]?.length < limit

  if (isLoading && projects.length === 0) return <ProjectListSkeleton />
  if (error) return <p className="text-red-500">{t('failed')}</p>

  return (
    <div
      className={cn(
        'rounded-2xl',
        !transparent &&
          'bg-gradient-to-br from-white via-white to-gray-50 border border-gray-200/50 shadow-xl shadow-gray-200/50 backdrop-blur-xl',
      )}
    >
      {showTitle && (
        <div className="mb-3">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <PaletteIcon className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-2xl font-black text-gray-900">
              {t('latest_creations.title')}
            </h2>
          </div>
          <p className="text-gray-500 ml-[44px]">
            {t('latest_creations.subtitle')}
          </p>
        </div>
      )}

      <div className="flex gap-2 ml-[16px] mb-8 justify-center items-center">
        {/* {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setFilter(item.id)}
            className={cn(
              'px-4 py-1.5 rounded-full text-sm font-medium transition-all',
              filter === item.id
                ? `bg-gradient-to-br ${item.color} text-white shadow-sm`
                : `${item.bgColor} ${item.hoverColor}`,
              filter === item.id
                ? 'text-white'
                : item.id === 'all'
                  ? 'text-gray-600'
                  : 'text-purple-600',
            )}
          >
            {t(`filters.${item.id}`)}
          </button>
        ))} */}
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setFilter(item.id)}
            className={cn(
              'px-3 md:px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 border border-transparent',
              'border border-white/10',
              filter === item.id
                ? 'bg-[#15F1FD] text-black font-extrabold border-none'
                : 'bg-gray-800/40 text-gray-300 hover:bg-gray-700/60 hover:text-cyan-300 hover:border-cyan-500/30 hover:shadow-[0_0_10px_rgba(45,212,191,0.3)]',
            )}
          >
            {t(`filters.${item.id}`)}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map((project) => (
          <ProjectCard
            key={project._id.toString()}
            project={project}
            isOwner={project.user_id === user?._id.toString()}
            useHref
            onClickLink={(e) => {
              if (!user) {
                e.preventDefault()
                pushModal('LoginModal', { redirectUrl: '/' })
                return
              }
              // add mixpanel event
              sendToMixpanel(
                MIXPANEL_CLIENT_EVENT_NAMES.VIEW_LATEST_PROJECTS,
                {
                  project_id: project._id.toString(),
                },
                user._id.toString(),
              )
            }}
            transparent={transparent}
          />
        ))}
      </div>

      <div className="flex justify-center mt-6">
        {!isReachingEnd && (
          // <Button
          //   variant="outline"
          //   onClick={() => setSize(size + 1)}
          //   disabled={isValidating}
          // >
          //   {isValidating ? t('loading') : t('load_more')}
          // </Button>
          <LoadMoreBtn
            onClick={() => setSize(size + 1)}
            disabled={isValidating}
          />
        )}
        {isReachingEnd && projects.length > 0 && (
          <p className="text-gray-500">{t('no_more')}</p>
        )}
      </div>
    </div>
  )
}

export const UserProjectList = ({
  transparent = false,
  showTitle = true,
}: { transparent?: boolean; showTitle?: boolean }) => {
  const { data: user } = useCurrentUser()
  const limit = 20 // Default projects per page
  const t = useTranslations('projects')

  const { data, error, size, setSize, isLoading, isValidating, mutate } =
    useSWRInfinite((pageIndex) => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (pageIndex * limit).toString(),
      })
      return `/api/me/projects?${params}`
    }, fetcher)

  const projects = data ? data.flat() : []
  const isReachingEnd = data && data[data.length - 1]?.length < limit

  if (isLoading && projects.length === 0) return <ProjectListSkeleton />
  if (error) return <p className="text-red-500">{t('failed')}</p>
  if (!projects || projects.length === 0) return null

  return (
    <div
      className={cn(
        'rounded-2xl',
        !transparent &&
          'bg-gradient-to-br from-white via-white to-gray-50 border border-gray-200/50 p-5 shadow-xl shadow-gray-200/50 backdrop-blur-xl',
      )}
    >
      {showTitle && (
        <div className="mb-3">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <PaletteIcon className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-2xl font-black text-gray-900">
              {t('my_creations.title')}
            </h2>
          </div>
          <p className="text-gray-500 ml-[44px]">
            {t('my_creations.subtitle')}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map((project) => (
          <ProjectCard
            key={project._id.toString()}
            project={project}
            isOwner={project.user_id === user?._id.toString()}
            showActions
            mutate={mutate}
            useHref
            transparent={transparent}
          />
        ))}
      </div>

      <div className="flex justify-center mt-6">
        {!isReachingEnd && (
          // <Button
          //   variant="outline"
          //   onClick={() => setSize(size + 1)}
          //   disabled={isValidating}
          // >
          //   {isValidating ? t('loading') : t('load_more')}
          // </Button>
          <LoadMoreBtn
            onClick={() => setSize(size + 1)}
            disabled={isValidating}
          />
        )}
        {isReachingEnd && projects.length > 0 && (
          <p className="text-gray-500">{t('no_more')}</p>
        )}
      </div>
    </div>
  )
}

const StatusBadge = ({ status }: { status: string }) => {
  const t = useTranslations('projects.status')

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'live':
        return 'text-green-600 bg-green-50'
      case 'in progress':
        return 'text-blue-600 bg-blue-50'
      case 'draft':
        return 'text-gray-600 bg-gray-50'
      default:
        return 'text-gray-600 bg-gray-50'
    }
  }

  const getTranslatedStatus = (status: string) => {
    switch (status.toLowerCase()) {
      case 'live':
        return t('live')
      case 'in progress':
        return t('in_progress')
      case 'draft':
        return t('draft')
      default:
        return status
    }
  }

  return (
    <span
      className={cn(
        'px-3 py-1 rounded-full text-sm font-medium capitalize',
        getStatusColor(status),
      )}
    >
      {getTranslatedStatus(status)}
    </span>
  )
}

const DisabledProjectCardContent = ({ project }: { project: UserProject }) => {
  const t = useTranslations('project-list')

  return (
    <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/80 saturate-50">
      <div className="relative">
        <div className="w-full aspect-video rounded-lg flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200/50">
          <div className="flex flex-col items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-gray-200/80 flex items-center justify-center">
              <LockIcon className="w-6 h-6 text-gray-400" />
            </div>
          </div>
        </div>

        <div className="absolute top-3 right-3">
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-200/80 text-gray-600 backdrop-blur-sm">
            {t('private.badge')}
          </span>
        </div>
      </div>

      <h3 className="text-lg font-semibold mt-4 mb-1 text-gray-500">
        {t('private.title')}
      </h3>

      <div className="flex items-center gap-2 text-sm text-gray-400">
        {project.create_time && (
          <time>{format(new Date(project.create_time), 'MMM d, yyyy')}</time>
        )}
      </div>

      <div className="flex items-center gap-2 mt-3">
        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
          <span className="text-xs text-gray-500">?</span>
        </div>
        <span className="text-sm text-gray-400">
          {project.user?.name || ''}
        </span>
      </div>
    </div>
  )
}

const ProjectCardContent = ({
  project,
  isPrivate,
  isOwner,
  showActions = false,
  showAvatar = true,
  mutate,
  cardClassName,
  projectNameClassName,
  projectTimeClassName,
  transparent = false,
}: {
  project: UserProject
  isPrivate: boolean
  isOwner: boolean
  showActions?: boolean
  showAvatar?: boolean
  mutate: SWRInfiniteKeyedMutator<any[]> | undefined
  cardClassName?: string
  projectNameClassName?: string
  projectTimeClassName?: string
  transparent?: boolean
}) => {
  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const t = useTranslations('project-list')
  const setIsAnyAlertDialogOpen = useGlobalStore(
    (state) => state.setIsAnyAlertDialogOpen,
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  useEffect(() => {
    setIsAnyAlertDialogOpen(showDeleteConfirm)
  }, [showDeleteConfirm, setIsAnyAlertDialogOpen])

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      const response = await fetch(`/api/me/project?projectId=${project._id}`, {
        method: 'DELETE',
      })

      if (response.ok) {
        setDropdownOpen(false)
        if (mutate) await mutate()
        toast.success(t('toast.delete_success'))
      } else {
        toast.error(t('toast.delete_error'))
      }
    } catch (error) {
      toast.error(t('toast.delete_error'))
    } finally {
      setIsDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const toggleDropdown = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDropdownOpen(!isDropdownOpen)
  }

  return (
    <>
      <div
        className={cn(
          'p-4 rounded-xl transition-all duration-200 relative bg-gray-800/30',
          cardClassName,
          !transparent && 'border border-gray-200',
          'cyber-card hover:border-cyber-blue border-none p-0',
        )}
      >
        <div className="relative">
          <div className="w-full aspect-video rounded-lg rounded-b-none flex items-center justify-center overflow-hidden bg-gradient-to-br from-purple-500/10 to-indigo-600/10 bg-gray-300">
            {project.thumbnail ? (
              <img
                src={project.thumbnail}
                alt={project.project_name}
                className="w-full object-cover transition-opacity "
              />
            ) : (
              <Layout className="w-8 h-8 text-indigo-600/40" />
            )}
          </div>

          {isPrivate && (
            <div className="absolute top-3 left-3">
              <div className="w-8 h-8 rounded-lg bg-white/80 backdrop-blur flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.08)] ring-1 ring-black/[0.04]">
                <LockIcon className="w-4 h-4 text-gray-700" />
              </div>
            </div>
          )}

          <div className="absolute top-3 right-3">
            {showActions && isOwner && (
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={toggleDropdown}
                  className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                >
                  <EllipsisVertical className="w-4 h-4 text-gray-700" />
                </button>

                {isDropdownOpen && (
                  <div className="absolute right-0 mt-1 rounded-xl bg-white shadow-lg ring-1 ring-black/5 p-1">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setShowDeleteConfirm(true)
                      }}
                      className="w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    >
                      <Trash2 className="w-4 h-4 shrink-0" />
                      <span className="truncate">{t('actions.delete')}</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div
            className={cn(
              'absolute top-3 flex gap-1',
              showActions ? 'right-[52px]' : 'right-3',
            )}
          >
            {project.build_option === 'prototype' && (
              <span
                className={cn(
                  'px-3 py-1 rounded-full text-sm font-medium capitalize bg-purple-50 text-purple-600',
                )}
              >
                {t('prototype')}
              </span>
            )}
            <StatusBadge status={project.status || 'Draft'} />
          </div>
        </div>

        <div className="px-6 py-4">
          <h3
            className={cn(
              'text-lg font-semibold mb-1 line-clamp-2 text-white',
              projectNameClassName,
            )}
          >
            {project.project_name}
          </h3>

          <div className="flex items-center gap-2 text-sm text-gray-400">
            {project.create_time && (
              <time
                className={cn('text-sm text-gray-400', projectTimeClassName)}
              >
                {format(new Date(project.create_time), 'MMM d, yyyy')}
              </time>
            )}
          </div>

          {showAvatar && (
            <div className="flex items-center gap-2 mt-3">
              {project.user?.avatar ? (
                <img
                  src={project.user.avatar}
                  alt={project.user.name || ''}
                  width={24}
                  height={24}
                  className="rounded-full"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-xs text-gray-500">
                    {project.user?.name?.charAt(0) || '?'}
                  </span>
                </div>
              )}
              <span className="text-sm text-gray-600">
                {project.user?.name || ''}
              </span>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={showDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('delete_confirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('delete_confirm.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="text-muted-foreground"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setShowDeleteConfirm(false)
              }}
              disabled={isDeleting}
            >
              {t('delete_confirm.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleDelete()
              }}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  <span>{t('actions.deleting')}</span>
                </div>
              ) : (
                t('delete_confirm.confirm')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export const ProjectCard = ({
  project,
  isOwner,
  showActions = false,
  mutate,
  useHref = true,
  showAvatar = true,
  cardClassName,
  projectNameClassName,
  projectTimeClassName,
  linkToNewTab = true,
  onClickLink,
  transparent = false,
}: {
  project: UserProject
  isOwner: boolean
  showActions?: boolean
  mutate?: SWRInfiniteKeyedMutator<any[]>
  useHref?: boolean
  showAvatar?: boolean
  cardClassName?: string
  projectNameClassName?: string
  projectTimeClassName?: string
  linkToNewTab?: boolean
  onClickLink?: (e: React.MouseEvent) => void
  transparent?: boolean
}) => {
  const isPrivate = project.visibility === 'private'
  const isDisabled = isPrivate && !isOwner

  if (isDisabled) {
    return <DisabledProjectCardContent project={project} />
  }

  const content = (
    <ProjectCardContent
      project={project}
      isPrivate={isPrivate}
      isOwner={isOwner}
      showActions={showActions}
      mutate={mutate}
      cardClassName={cardClassName}
      projectNameClassName={projectNameClassName}
      projectTimeClassName={projectTimeClassName}
      showAvatar={showAvatar}
      transparent={transparent}
    />
  )

  if (useHref) {
    return (
      <Link
        href={`/project?id=${project._id}`}
        target={linkToNewTab ? '_blank' : '_self'}
        rel="noopener noreferrer"
        onClick={onClickLink}
      >
        {content}
      </Link>
    )
  }

  return content
}
